"use client";

import Link from "next/link";
import { messages } from "@/lib/i18n";
import { useParams } from "next/navigation";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";

export default function Footer() {
  const params = useParams();
  const locale = (params?.locale ?? "en") as "en" | "ar" | "fr";
  const t = messages[locale] ?? messages.en;

  // Phone numbers are the same for all languages
  const phones = [
    "+250795826974",
    "+963968900919",
  ];

  return (
    <footer className="relative w-full bg-black/90 text-white border-t border-yellow-500/20">
      <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row justify-between gap-8">

        {/* Company Info */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-bold text-yellow-400">
            SmartLine
          </h2>

          <p className="text-gray-300 max-w-sm">
            {t.footer.description ??
              "Your go-to solution for modern web projects and creative services."}
          </p>

          {/* Phone Numbers */}
          <div className="flex flex-col gap-4">
            {phones.map((phone, idx) => (
              <p
                key={idx}
                className="flex items-center gap-3 text-gray-300"
              >
                <FaPhone className="text-yellow-400" />

                <a
                  href={`tel:${phone}`}
                  className="hover:text-yellow-400 transition"
                >
                  {phone}
                </a>
              </p>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xl font-semibold text-yellow-400 mb-2">
            {t.footer.linksTitle ?? "Quick Links"}
          </h3>

          <Link
            href="#services"
            className="hover:text-yellow-400 transition"
          >
            {t.nav.services}
          </Link>

          <Link
            href="#case-studies"
            className="hover:text-yellow-400 transition"
          >
            {t.nav.projects}
          </Link>

          <Link
            href="/about"
            className="hover:text-yellow-400 transition"
          >
            {t.nav.about}
          </Link>

          <Link
            href="/contact"
            className="hover:text-yellow-400 transition"
          >
            {t.nav.contact}
          </Link>
        </div>

        {/* Contact */}
        <div className="flex flex-col gap-2">
          <h3 className="text-xl font-semibold text-yellow-400 mb-2">
            {t.footer.contactTitle ?? "Contact"}
          </h3>

          {/* Social Media & Email */}
          <div className="flex justify-center items-center gap-6 mt-4 text-2xl md:text-3xl">

            {/* Email */}
            <a
              href="mailto:Smartline@smartline-sy.com"
              aria-label="Email SmartLine"
              title="Smartline@smartline-sy.com"
              className="text-[#EA4335] hover:opacity-80 transition"
            >
              <FaEnvelope />
            </a>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/share/17tdVbooPi/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1877F2] hover:opacity-80 transition"
            >
              <FaFacebookF />
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/smartline.rw/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#E4405F] hover:opacity-80 transition"
            >
              <FaInstagram />
            </a>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/in/fadi-shalhoub-93a0803a3"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0A66C2] hover:opacity-80 transition"
            >
              <FaLinkedinIn />
            </a>

          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-yellow-500/10 text-gray-400 text-sm text-center py-4">
        &copy; {new Date().getFullYear()} SmartLine.{" "}
        {t.footer.rights ?? "All rights reserved."}
      </div>
    </footer>
  );
}